import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type CanopySizeTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface CanopySizeFieldProps {
  onChange: (treeDescriptor: TreeDescriptor) => void;
  treeDescriptor: TreeDescriptor;
}

export function CanopySizeField<T extends CanopySizeTreePropertyDescriptor>({
  onChange,
  treeDescriptor,
}: CanopySizeFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.canopy_size,
    },
  );

  const propertyBoundary = treeDescriptor.properties.get(
    TreePropertyType.canopy_size,
  ) as T | undefined;

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.canopy_size,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <div>
        <FieldLabel isValid={isValid}>
          {getText("Is the tree’s canopy greater than 4 metres in diameter?")}
        </FieldLabel>
        <p className="text-bodymd text-textSubdued">
          {getText(
            "If the canopy is irregularly shaped, measure its longest length.",
          )}
        </p>
      </div>

      <div className="flex gap-2">
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("yes");
          }}
          pressed={propertyBoundary?.value === "yes"}
          variant="rounded"
        >
          {getText("Yes")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("no");
          }}
          pressed={propertyBoundary?.value === "no"}
          variant="rounded"
        >
          {getText("No")}
        </Toggle>
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
